import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiLoadingStatus } from "enum/api-loading-status.enum";
import { ISalesAccount } from "models/salesAccount/salesAccount.model";
import { fetchCompanyAddresses } from "services/api/companyAddresses";
import { fetchCompanyContacts } from "services/api/companyContacts";
import {
  updateEnablePrintPackingSlip,
  fetchSettings,
  updateGroupBySKUForPackingSlip,
} from "services/api/settings";
import { AppThunk, RootState } from "store";
import { ISettingsSlice } from "./settingsSlice.interface";
import { SettingsSliceInitialState } from "./settingsSliceInitialState.const";
import { toast } from "react-hot-toast";

export const settingsSlice = createSlice({
  name: "settingsSlice",
  initialState: SettingsSliceInitialState,
  reducers: {
    setSettingsData: (state, action: PayloadAction<ISalesAccount>) => {
      state.salesAccount = action.payload;
      state.settings = action.payload.defaultSettings;
      state.loadingBaseStatus = ApiLoadingStatus.SUCCESS;
    },
    setSettingsLoadingStatus: (
      state,
      action: PayloadAction<ApiLoadingStatus>
    ) => {
      state.loadingBaseStatus = action.payload;
    },
    setCompanyAddresses: (
      state,
      action: PayloadAction<ISettingsSlice["companyAddresses"]>
    ) => {
      state.companyAddresses = action.payload;
      state.loadingCompanyAddressesStatus = ApiLoadingStatus.SUCCESS;
    },
    setCompanyAddressesLoadingStatus: (
      state,
      action: PayloadAction<ApiLoadingStatus>
    ) => {
      state.loadingCompanyAddressesStatus = action.payload;
    },
    setCompanyContacts: (
      state,
      action: PayloadAction<ISettingsSlice["companyContacts"]>
    ) => {
      state.companyContacts = action.payload;
      state.loadingCompanyContactsStatus = ApiLoadingStatus.SUCCESS;
    },
    setCompanyContactsLoadingStatus: (
      state,
      action: PayloadAction<ApiLoadingStatus>
    ) => {
      state.loadingCompanyContactsStatus = action.payload;
    },
    setPrintPackingSlip: (state, action: PayloadAction<boolean>) => {
      if (state.settings) {
        state.settings.enablePrintPackingSlip = action.payload;
      } else {
        console.error("state.settings is undefined");
      }
    },
    setShouldGroupSKUForPackingSlip: (
      state,
      action: PayloadAction<boolean>
    ) => {
      if (state.settings) {
        state.settings.shouldGroupItemsBySkuForPackingSlip = action.payload;
      } else {
        console.error("state.settings is undefined");
      }
    },
  },
});

export const {
  setSettingsData,
  setSettingsLoadingStatus,
  setCompanyAddresses,
  setCompanyAddressesLoadingStatus,
  setCompanyContacts,
  setCompanyContactsLoadingStatus,
  setPrintPackingSlip,
  setShouldGroupSKUForPackingSlip,
} = settingsSlice.actions;

export const fetchSettingsThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(setSettingsLoadingStatus(ApiLoadingStatus.LOADING));

    const {
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    const { data, error } = await fetchSettings(salesAccountUUID);

    if (!error && data) {
      dispatch(setSettingsData(data));
    }

    if (error) {
      dispatch(setSettingsLoadingStatus(ApiLoadingStatus.FAILED));
    }
  };
};

export const fetchCompanyAddressesThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(setCompanyAddressesLoadingStatus(ApiLoadingStatus.LOADING));

    const {
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    if (salesAccountUUID) {
      const { data, error } = await fetchCompanyAddresses(salesAccountUUID);

      if (!error || data) {
        dispatch(setCompanyAddresses(data));
      }

      if (error) {
        dispatch(setCompanyAddressesLoadingStatus(ApiLoadingStatus.FAILED));
      }
    }
  };
};

export const fetchCompanyContactsThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(setCompanyContactsLoadingStatus(ApiLoadingStatus.LOADING));

    const {
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    if (salesAccountUUID) {
      const { data, error } = await fetchCompanyContacts(salesAccountUUID);

      if (!error || data) {
        dispatch(setCompanyContacts(data));
      }

      if (error) {
        dispatch(setCompanyContactsLoadingStatus(ApiLoadingStatus.FAILED));
      }
    }
  };
};

export const updatePrintPackingSlipSetting = (): AppThunk => {
  return async (dispatch, getState) => {
    const {
      settingsSlice: { settings },
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    const { enablePrintPackingSlip } = settings;

    if (salesAccountUUID) {
      const { data, error } = await updateEnablePrintPackingSlip(
        salesAccountUUID,
        enablePrintPackingSlip
      );

      if (data) {
        toast.success("Saved packing slip setting");
      } else {
        toast.error("Something went wrong!");
      }
      console.log("data", data);
      console.log("error", error);
    }
  };
};

export const updateGroupSKUForPackingSlip = (): AppThunk => {
  return async (dispatch, getState) => {
    const {
      settingsSlice: { settings },
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    const { shouldGroupItemsBySkuForPackingSlip } = settings;

    if (salesAccountUUID) {
      const { data, error } = await updateGroupBySKUForPackingSlip(
        salesAccountUUID,
        shouldGroupItemsBySkuForPackingSlip
      );

      if (data) {
        toast.success("Updated settings.");
      } else {
        toast.error("Something went wrong!");
      }
      console.log("data", data);
      console.log("error", error);
    }
  };
};
